import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Wheat } from 'lucide-react'

const F = 'Roboto, sans-serif'
const FIBER = '#20C997'

interface Props {
  open: boolean
  onClose: () => void
}

// Польза при 25–35 г/день против низкого потребления.
const BENEFITS: { emoji: string; text: string }[] = [
  { emoji: '📉', text: 'Риск сердечно-сосудистых заболеваний ниже примерно на 15–30%' },
  { emoji: '📉', text: 'Риск сахарного диабета 2 типа ниже примерно на 15–25%' },
  { emoji: '📉', text: 'Риск колоректального рака ниже примерно на 10–20%' },
  { emoji: '📉', text: 'Смертность от всех причин ниже примерно на 15–30%' },
  { emoji: '🍽️', text: 'Лучше насыщение и контроль аппетита' },
  { emoji: '🚽', text: 'Более регулярный стул' },
  { emoji: '🦠', text: 'Более здоровая микрофлора кишечника' },
]

// «Добавьте 2–3 пункта в день» — продукт → клетчатка.
const TOP_UP: { food: string; fiber: string }[] = [
  { food: '100 г чечевицы (вареной)', fiber: '~8 г' },
  { food: '100 г фасоли (вареной)', fiber: '~6–8 г' },
  { food: '200 г капусты', fiber: '~5 г' },
  { food: '200 г брокколи', fiber: '~5–6 г' },
  { food: '100 г малины', fiber: '~6–7 г' },
  { food: '100 г чёрной смородины', fiber: '~6–7 г' },
  { food: '50 г овсянки (сухой)', fiber: '~5 г' },
  { food: '100 г гречки (вареной)', fiber: '~3–4 г' },
  { food: '1 среднее яблоко', fiber: '~4 г' },
]

const TOP_PRODUCTS = [
  '🥇 Чечевица', '🥈 Фасоль', '🥉 Горох', '4. Чёрная смородина', '5. Малина',
  '6. Овсянка', '7. Брокколи', '8. Белокочанная капуста', '9. Гречка', '10. Яблоки',
]

const LESS_THAN_THINK: string[] = [
  'Белый рис — мало',
  'Бананы — умеренно (~2–3 г на 100 г)',
  'Картофель — умеренно (~2 г на 100 г)',
  'Огурцы — мало',
  'Помидоры — мало',
  'Листовой салат — мало',
  'Соки — почти нет',
  'Орехи — есть, но не так много, как многие считают (5–10 г на 100 г, а порции маленькие)',
]

const GUIDE = [
  '300–500 г овощей',
  '1–2 порции фруктов или ягод',
  '1–2 порции бобовых или цельнозерновых',
]

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'white', border: '1px solid rgba(26,58,92,0.06)', borderRadius: 14, padding: 12, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  )
}

/**
 * Справка по клетчатке: что это, зачем нужна и как легко добрать.
 * Открывается тапом по (?) рядом с полем «Клетчатка» в шторке питания.
 */
export function FiberInfoSheet({ open, onClose }: Props) {
  const dragControls = useDragControls()
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 420,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="fiber-info-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragListener={false} dragControls={dragControls}
            dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            {/* Hero */}
            <div style={{ position: 'relative', padding: '8px 20px 6px' }}>
              <button
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 4, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingRight: 40, paddingTop: 4 }}>
                <div style={{
                  width: 62, height: 62, borderRadius: 18,
                  background: `linear-gradient(135deg, ${FIBER}, #12A074)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 8px 22px rgba(32,201,151,0.35), 0 0 0 1.5px rgba(255,255,255,0.6) inset',
                }}>
                  <Wheat size={30} color="white" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA5B4', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Справка
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#1C2D41', marginTop: 2 }}>Клетчатка</div>
                  <div style={{ fontSize: 11, color: '#6B7B8D', marginTop: 2 }}>Зачем она нужна и как легко добрать</div>
                </div>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '6px 16px 0', minHeight: 0 }}>
              {/* Что это */}
              <div style={{
                background: 'rgba(32,201,151,0.07)', border: '1px solid rgba(32,201,151,0.20)',
                borderRadius: 14, padding: 12, marginBottom: 12,
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', marginBottom: 4 }}>Что это?</div>
                <p style={{ fontSize: 12.5, color: '#3D4A5C', margin: 0, lineHeight: 1.55 }}>
                  Клетчатка — это разновидность углеводов, которую организм почти не переваривает. Она проходит
                  через кишечник и служит пищей для полезных бактерий.
                </p>
              </div>

              {/* Зачем нужна */}
              <Card title="Зачем нужна">
                <p style={{ fontSize: 12, color: '#6B7B8D', margin: '0 0 8px', lineHeight: 1.5 }}>
                  При потреблении <b style={{ color: '#1C2D41' }}>25–35 г</b> клетчатки в день (против низкого потребления):
                </p>
                {BENEFITS.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0' }}>
                    <span style={{ flexShrink: 0, fontSize: 14, lineHeight: 1.4 }}>{b.emoji}</span>
                    <span style={{ fontSize: 12.5, color: '#1C2D41', lineHeight: 1.45 }}>{b.text}</span>
                  </div>
                ))}
              </Card>

              {/* Как добрать */}
              <Card title="Как легко добрать">
                <p style={{ fontSize: 12, color: '#6B7B8D', margin: '0 0 8px', lineHeight: 1.5 }}>
                  Добавьте в день 2–3 пункта:
                </p>
                {TOP_UP.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    padding: '7px 0', borderTop: i === 0 ? 'none' : '1px solid rgba(26,58,92,0.06)',
                  }}>
                    <span style={{ fontSize: 12.5, color: '#1C2D41', lineHeight: 1.35 }}>{r.food}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: FIBER, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{r.fiber}</span>
                  </div>
                ))}
              </Card>

              {/* Топ продуктов */}
              <Card title="Топ продуктов по клетчатке">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {TOP_PRODUCTS.map((p, i) => (
                    <span key={i} style={{
                      fontSize: 12, color: '#1C2D41', background: 'rgba(32,201,151,0.10)',
                      border: '1px solid rgba(32,201,151,0.22)', borderRadius: 9, padding: '4px 9px',
                    }}>{p}</span>
                  ))}
                </div>
              </Card>

              {/* Где меньше, чем думают */}
              <Card title="Где клетчатки меньше, чем многие думают">
                {LESS_THAN_THINK.map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0' }}>
                    <span style={{ flexShrink: 0, width: 5, height: 5, borderRadius: '50%', background: '#C4CCD6', marginTop: 7 }} />
                    <span style={{ fontSize: 12.5, color: '#3D4A5C', lineHeight: 1.45 }}>{t}</span>
                  </div>
                ))}
              </Card>

              {/* Практический ориентир */}
              <div style={{
                background: 'rgba(108,92,231,0.06)', border: '1px solid rgba(108,92,231,0.18)',
                borderRadius: 14, padding: 12, marginBottom: 14,
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', marginBottom: 6 }}>Практический ориентир</div>
                <p style={{ fontSize: 12, color: '#6B7B8D', margin: '0 0 6px' }}>Съедайте ежедневно:</p>
                {GUIDE.map((g, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                    <span style={{ flexShrink: 0, color: '#6C5CE7', fontWeight: 800 }}>•</span>
                    <span style={{ fontSize: 12.5, color: '#1C2D41' }}>{g}</span>
                  </div>
                ))}
                <p style={{ fontSize: 12.5, color: '#3D4A5C', margin: '8px 0 0', lineHeight: 1.5 }}>
                  Это обычно даёт <b style={{ color: FIBER }}>25–35 г</b> клетчатки в сутки — как раз большинство рекомендаций.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
